/**
 * PRODUCT — the product profile card: category, clear/texture layers, per-layer opacity.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12 / AUD-2 RC-4). The seam is the file's own JSX banner;
 * nothing inside the card changed — every `data-testid`, every `aria-label`, the UV-varnish popover
 * and its 27 calibration sliders are byte-identical.
 * 
 * `VARNISH_SLIDERS` and the two varnish colour helpers moved with the popover: it is their only
 * reader.
 */
import type { ImageAsset } from "@genz/platform";
import type { VarnishParams } from "@genz/engine-bind";
import { Icon } from "../../ui/Icon.js";
import { fillStackForProfile, type ProductCategory } from "../compositing.js";
import { PRODUCT_CATEGORY_LABEL } from "../controlLabels.js";
import type { ComposeReason } from "../maskEditorTypes.js";
import { VARNISH_DEFAULT } from "../useLayerAssets.js";

/** UV calibration sliders: [key, label, min, max, scale, formatter]. */
const VARNISH_SLIDERS: ReadonlyArray<readonly [keyof VarnishParams, string, number, number, number, (v: number) => string]> = [
  ["intensity", "Intensity", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["soften", "Soften", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["depth", "Depth", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["embossScale", "Emboss size", 0, 400, 0.01, (v) => `${v.toFixed(2)}×`],
  ["colorPreserve", "Keep colour", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["gloss", "Gloss", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["lightDeg", "Light angle", 0, 360, 1, (v) => `${Math.round(v)}°`],
  ["gradTop", "Highlight top", 0, 150, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["gradBottom", "Highlight bottom", 0, 150, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["grainScale", "Grain size (lower = finer)", 1, 400, 0.01, (v) => `${v.toFixed(2)}×`],
  ["grainOpacity", "Grain amount", 0, 50, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["grainContrast", "Grain contrast", 0, 300, 0.01, (v) => `${v.toFixed(2)}×`],
  ["grainGradient", "Grain falloff", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["grainBevel", "Grain bevel", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["grainRound", "Grain roundness", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["surfaceGloss", "Surface sheen", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["outlineBevel", "Outline bevel", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["outlineShadow", "Outline shadow", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["outlineWidth", "Outline width", 0, 10, 0.001, (v) => `${(v * 100).toFixed(1)}%`],
  ["outlineSmooth", "Outline smooth", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["dropShadow", "Drop shadow", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["dropShadowDist", "Drop distance", 0, 10, 0.001, (v) => `${(v * 100).toFixed(1)}%`],
  ["shadowSkew", "Shadow skew", -180, 180, 1, (v) => `${Math.round(v)}°`],
  ["gradAngle", "Gradient angle", 0, 360, 1, (v) => `${Math.round(v)}°`],
  ["bevelCurve", "Bevel curve", 0, 100, 0.01, (v) => `${Math.round(v * 100)}%`],
  ["threshold", "Ink low", 0, 255, 1, (v) => `${Math.round(v)}`],
  ["thresholdHigh", "Ink high", 0, 255, 1, (v) => `${Math.round(v)}`],
];
const varnishRgbToHex = (a: [number, number, number]): string =>
  "#" + a.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const varnishHexToRgb = (h: string): [number, number, number] =>
  [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

export interface ProductProfileCardProps {
  locked?: boolean;
  profileCategory: ProductCategory;
  setProfileCategory: (v: ProductCategory) => void;
  profileClear: boolean;
  setProfileClear: React.Dispatch<React.SetStateAction<boolean>>;
  profileTexture: boolean;
  setProfileTexture: React.Dispatch<React.SetStateAction<boolean>>;
  layerOpacity: { phone: number; design: number; texture: number };
  setLayerOpacity: React.Dispatch<React.SetStateAction<{ phone: number; design: number; texture: number }>>;
  clearBg: Uint8Array | null;
  clearBgName: string;
  setClearBgName: (v: string) => void;
  clearBgInputRef: React.RefObject<HTMLInputElement | null>;
  clearBgUrlRef: React.RefObject<string | null>;
  texture: Uint8Array | null;
  textureName: string;
  setTextureName: (v: string) => void;
  textureInputRef: React.RefObject<HTMLInputElement | null>;
  textureUrlRef: React.RefObject<string | null>;
  applyClearBg: (b: Uint8Array | null) => void;
  applyTexture: (b: Uint8Array | null) => void;
  textureBlend: string;
  setTextureBlend: (v: string) => void;
  varnish: VarnishParams;
  setVarnish: React.Dispatch<React.SetStateAction<VarnishParams>>;
  showVarnishSettings: boolean;
  setShowVarnishSettings: React.Dispatch<React.SetStateAction<boolean>>;
  onProductImageFile: (file: File | null | undefined, set: (b: Uint8Array | null) => void, setName: (n: string) => void, label: string, urlRef?: React.MutableRefObject<string | null>) => void;
  pickImageFromUrl: (url: string, set: (b: Uint8Array | null) => void, setName: (n: string) => void, name: string, label: string) => Promise<void>;
  previewMockup: (uploadBake?: boolean, reason?: ComposeReason) => Promise<boolean>;
  textureOptions?: ImageAsset[];
  clearBgOptions?: ImageAsset[];
}

export function ProductProfileCard(p: ProductProfileCardProps) {
  const {
    locked, profileCategory, setProfileCategory, profileClear, setProfileClear,
    profileTexture, setProfileTexture, layerOpacity, setLayerOpacity, clearBg,
    clearBgName, setClearBgName, clearBgInputRef, clearBgUrlRef, texture,
    textureName, setTextureName, textureInputRef, textureUrlRef, applyClearBg,
    applyTexture, textureBlend, setTextureBlend, varnish, setVarnish,
    showVarnishSettings, setShowVarnishSettings, onProductImageFile, pickImageFromUrl, previewMockup,
    textureOptions, clearBgOptions,
  } = p;
  return (
    <>
      {/* Spec 019 US3: product profile — drives the fill stack (clear phone case adds a
          phone layer behind the design). Lives at the top of the right-side panel. */}
      <div className="mask-editor__profile" data-testid="product-profile">
        <span className="label">Product</span>
        <select
          className="input"
          value={profileCategory}
          onChange={(e) => setProfileCategory(e.target.value as ProductCategory)}
          disabled={locked}
          aria-label="Product category"
          data-testid="profile-category"
        >
          {/* WS-Z6: one label per value (`editor/controlLabels.ts`) — the rail's accordion
              head prints the same string, and two hand-written copies would drift. */}
          {(Object.entries(PRODUCT_CATEGORY_LABEL) as [ProductCategory, string][]).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {profileCategory === "phone_case" && (
          <label className="mask-editor__profile-clear">
            <input
              type="checkbox"
              checked={profileClear}
              onChange={(e) => setProfileClear(e.target.checked)}
              disabled={locked}
              data-testid="profile-clear"
            />
            Show the clear case under the print area
          </label>
        )}
        {profileCategory === "phone_case" && profileClear && (
          <div className="row mask-editor__inline-row" data-testid="clear-bg-row">
            {(clearBgOptions?.length ?? 0) > 0 && (
              <select className="input mask-editor__select--md" data-testid="clear-bg-select" disabled={locked} value=""
                onChange={(e) => { const o = clearBgOptions?.find((a) => a.id === e.target.value); clearBgUrlRef.current = o?.image_url ?? null; void pickImageFromUrl(o?.image_url ?? "", applyClearBg, setClearBgName, o?.name ?? "", "Clear-case background"); }}>
                <option value="">Library…</option>
                {clearBgOptions!.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            <input ref={clearBgInputRef} type="file" accept="image/*" className="is-hidden" disabled={locked}
              onChange={(e) => onProductImageFile(e.target.files?.[0], applyClearBg, setClearBgName, "Clear-case background", clearBgUrlRef)} data-testid="clear-bg-input" />
            <button type="button" className="btn btn--sm" disabled={locked} onClick={() => clearBgInputRef.current?.click()}
              data-testid="clear-bg-upload" title="Upload the phone/background image shown behind the design through the clear shell">
              <Icon name="phone" size={16} /> {clearBg ? "Change clear-case background" : "Upload clear-case background"}
            </button>
            {clearBg && (
              <>
                <span className="faint mask-editor__file-name" data-testid="clear-bg-name">{clearBgName || "background"}</span>
                <button type="button" className="btn btn--sm btn--ghost" disabled={locked} onClick={() => { applyClearBg(null); clearBgUrlRef.current = null; setClearBgName(""); void previewMockup(); }} data-testid="clear-bg-clear">Remove</button>
              </>
            )}
          </div>
        )}
        <label className="mask-editor__profile-texture">
          <input
            type="checkbox"
            checked={profileTexture || profileCategory === "uv_case"}
            onChange={(e) => setProfileTexture(e.target.checked)}
            disabled={locked || profileCategory === "uv_case"}
            data-testid="profile-texture"
          />
          Texture / effect layer{profileCategory === "uv_case" ? " (always on for UV)" : ""}
        </label>
        {(profileTexture || profileCategory === "uv_case") && (
          <div className="row mask-editor__inline-row" data-testid="texture-row">
            {(textureOptions?.length ?? 0) > 0 && (
              <select className="input mask-editor__select--md" data-testid="texture-select" disabled={locked} value=""
                onChange={(e) => { const o = textureOptions?.find((a) => a.id === e.target.value); textureUrlRef.current = o?.image_url ?? null; void pickImageFromUrl(o?.image_url ?? "", applyTexture, setTextureName, o?.name ?? "", "Texture"); }}>
                <option value="">Library…</option>
                {textureOptions!.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            <input ref={textureInputRef} type="file" accept="image/*" className="is-hidden" disabled={locked}
              onChange={(e) => onProductImageFile(e.target.files?.[0], applyTexture, setTextureName, "Texture", textureUrlRef)} data-testid="texture-input" />
            <button type="button" className="btn btn--sm" disabled={locked} onClick={() => textureInputRef.current?.click()}
              data-testid="texture-upload" title="Upload a texture/effect overlay composited over the design in the print area">
              <Icon name="sparkle" size={16} /> {texture ? "Change texture" : "Upload texture"}
            </button>
            {textureBlend === "uvvarnish" && !texture && (
              <span className="faint mask-editor__hint">UV varnish needs no texture — it's generated from the design.</span>
            )}
            {texture && (
              <span className="faint mask-editor__file-name" data-testid="texture-name">{textureName || "texture"}</span>
            )}
                <label className="faint mask-editor__inline-field" title="How the texture blends over the design in the print area">
                  Blend
                  <select
                    className="input mask-editor__select--sm"
                    data-testid="texture-blend"
                    disabled={locked}
                    value={textureBlend}
                    onChange={(e) => { setTextureBlend(e.target.value); void previewMockup(false); }}
                  >
                    <option value="normal">Normal</option>
                    <option value="multiply">Multiply</option>
                    <option value="screen">Screen</option>
                    <option value="overlay">Overlay</option>
                    <option value="softlight">Soft light</option>
                    <option value="uvvarnish">UV varnish (spot UV)</option>
                  </select>
                </label>
                {textureBlend === "uvvarnish" && (
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    aria-expanded={showVarnishSettings}
                    aria-label="UV varnish settings"
                    title="UV varnish settings"
                    data-testid="varnish-settings-toggle"
                    onClick={() => setShowVarnishSettings((v) => !v)}
                  >
                    <Icon name="settings" size={16} /> UV settings
                  </button>
                )}
                {textureBlend === "uvvarnish" && showVarnishSettings && (
                  <div className="mask-editor__uv-calibration mask-editor__subpanel mask-editor__subpanel--tight" data-testid="uv-calibration">
                    <div className="row row--between">
                      <span className="faint mask-editor__subpanel-title">UV varnish calibration</span>
                      <button type="button" className="btn btn--sm btn--ghost" disabled={locked}
                        onClick={() => setVarnish({ ...VARNISH_DEFAULT })} data-testid="varnish-reset"
                        title="Reset the UV varnish settings to their defaults">Reset</button>
                    </div>
                    {VARNISH_SLIDERS.map(([key, label, min, max, scale, fmt]) => {
                      const val = (varnish[key] ?? VARNISH_DEFAULT[key]) as number;
                      return (
                        <label key={key} className="mask-editor__opacity-row">
                          <span>{label} {fmt(val)}</span>
                          <input
                            type="range"
                            min={min}
                            max={max}
                            value={Math.round(val / scale)}
                            onChange={(e) => { const nv = Number(e.target.value) * scale; setVarnish((p) => ({ ...p, [key]: nv })); }}
                            disabled={locked}
                            aria-label={`Varnish ${label}`}
                            data-testid={`varnish-${key}`}
                          />
                        </label>
                      );
                    })}
                    {([["highlightColor", "Highlight tint"], ["shadowColor", "Shadow tint"]] as const).map(([key, label]) => {
                      const val = (varnish[key] ?? VARNISH_DEFAULT[key]) as [number, number, number];
                      return (
                        <label key={key} className="mask-editor__opacity-row">
                          <span>{label}</span>
                          <input
                            type="color"
                            value={varnishRgbToHex(val)}
                            onChange={(e) => { const rgb = varnishHexToRgb(e.target.value); setVarnish((p) => ({ ...p, [key]: rgb })); }}
                            disabled={locked}
                            aria-label={`Varnish ${label}`}
                            data-testid={`varnish-${key}`}
                            className="mask-editor__color-bar"
                          />
                        </label>
                      );
                    })}
                    {([["engrave", "Deboss (press in)"], ["selectInvert", "Invert selection"]] as const).map(([key, label]) => (
                      <label key={key} className="mask-editor__opacity-row mask-editor__opacity-row--inline">
                        <input
                          type="checkbox"
                          checked={!!(varnish[key] ?? VARNISH_DEFAULT[key])}
                          onChange={(e) => { const on = e.target.checked; setVarnish((p) => ({ ...p, [key]: on })); }}
                          disabled={locked}
                          data-testid={`varnish-${key}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
                {texture && (
                  <button type="button" className="btn btn--sm btn--ghost" disabled={locked} onClick={() => { applyTexture(null); textureUrlRef.current = null; setTextureName(""); void previewMockup(); }} data-testid="texture-clear">Remove</button>
                )}
          </div>
        )}
        <span className="mask-editor__profile-stack" data-testid="profile-stack">
          Layers (back→front): {fillStackForProfile({ category: profileCategory, clear: profileClear, texture: profileTexture }).join(" → ")} → shell → brand
        </span>
        {/* Per-layer opacity (US3 Layers) — baked into the mockup so the studio render
            composites each fill layer at this opacity. */}
        <div className="mask-editor__layer-opacity" data-testid="layer-opacity">
          {/* The clear-case PHONE is now a full-silhouette `behind` layer (opaque), not a
              print-area fill — so no phone opacity slider (it would be a no-op). */}
          {/* A UvVarnish texture is driven by its own "Varnish intensity" slider (up in the
              texture row), not by layer opacity — so hide the redundant texture-opacity row. */}
          {fillStackForProfile({ category: profileCategory, clear: profileClear, texture: profileTexture })
            .filter((layer) => layer !== "phone")
            .filter((layer) => !(layer === "texture" && textureBlend === "uvvarnish"))
            .map((layer) => (
            <label key={layer} className="mask-editor__opacity-row">
              {layer} {Math.round(layerOpacity[layer] * 100)}%
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(layerOpacity[layer] * 100)}
                onChange={(e) => setLayerOpacity((o) => ({ ...o, [layer]: Number(e.target.value) / 100 }))}
                disabled={locked}
                aria-label={`${layer} opacity`}
                data-testid={`opacity-${layer}`}
              />
            </label>
          ))}
        </div>
      </div>
    </>
  );
}
